import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Chip from "@material-ui/core/Chip";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	chips: {
		display: "flex",
		flexWrap: "wrap",
		gap: "6px",
		maxWidth: "100%",
		alignItems: "flex-start",
		padding: "4px 0",
	},
	chip: {
		margin: 1,
		maxWidth: "100%",
		height: "auto",
		borderRadius: "12px",
		boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
		"& .MuiChip-label": {
			whiteSpace: "normal",
			wordBreak: "break-word",
			lineHeight: "1.2",
			padding: "6px 10px",
			fontSize: "0.8rem",
			fontWeight: "500",
			color: "white",
		},
		"& .MuiChip-deleteIcon": {
			color: "rgba(255, 255, 255, 0.8)",
			"&:hover": {
				color: "white",
			},
		},
	},
	selectContainer: {
		"& .MuiSelect-select": {
			minHeight: "50px",
			display: "flex",
			alignItems: "flex-start",
			padding: "8px 12px",
			borderRadius: "8px",
		},
		"& .MuiOutlinedInput-root": {
			borderRadius: "8px",
		},
	},
	menuItem: {
		padding: "8px 16px",
		"&:hover": {
			backgroundColor: "#f5f5f5",
		},
	},
	menuItemSelected: {
		backgroundColor: "#e3f2fd !important",
		color: "#1976d2",
		fontWeight: "500",
		"&:hover": {
			backgroundColor: "#bbdefb !important",
		},
	},
	menuItemIcon: {
		marginRight: "8px",
		fontSize: "16px",
	},
	placeholder: {
		color: "#999",
		fontStyle: "italic",
		fontSize: "0.9rem",
	},
	selectedCount: {
		color: "#1976d2",
		fontWeight: "500",
		fontSize: "0.8rem",
		marginLeft: "8px",
	},
}));

const QueueSelect = ({ selectedQueueIds, onChange, multiple = true, title = i18n.t("queueSelect.inputLabel") }) => {
	const classes = useStyles();
	const [queues, setQueues] = useState([]);

	useEffect(() => {

		fetchQueues();

	}, []);

	const fetchQueues = async () => {
		try {
			const { data } = await api.get("/queue");
			setQueues(data);
		} catch (err) {
			toastError(err);
		}
	}

	const handleChange = e => {
		onChange(e.target.value);
	};

	return (
		<div >
			<FormControl fullWidth margin="dense" variant="outlined" className={classes.selectContainer}>
				<InputLabel shrink={selectedQueueIds ? true : false} >{title}</InputLabel>
				<Select
					label={title}
					multiple={multiple}
					labelWidth={60}
					value={selectedQueueIds || (multiple ? [] : '')}
					onChange={handleChange}
					MenuProps={{
						anchorOrigin: {
							vertical: "bottom",
							horizontal: "left",
						},
						transformOrigin: {
							vertical: "top",
							horizontal: "left",
						},
						getContentAnchorEl: null,
					}}
					renderValue={selected => {
						// ✅ VALIDACIÓN: Manejar valores nulos o vacíos
						const placeholderText = multiple ? "Seleccionar departamentos..." : "Seleccionar departamento...";
						if (!selected || selected === null || selected === undefined || selected === '') {
							return <span className={classes.placeholder}>{placeholderText}</span>;
						}
						
						if (multiple) {
							// ✅ VALIDACIÓN: Verificar que selected sea un array
							if (!Array.isArray(selected) || selected.length === 0) {
								return <span className={classes.placeholder}>{placeholderText}</span>;
							}
							
							return (
								<div className={classes.chips}>
									{selected.map(id => {
										// ✅ VALIDACIÓN: Verificar que el ID sea válido
										if (!id || id === null || id === undefined) return null;
										
										const queue = queues.find(q => q.id === id);
										return queue ? (
											<Chip
												key={id}
												style={{ backgroundColor: queue.color }}
												variant="outlined"
												label={queue.name}
												className={classes.chip}
												onDelete={() => {
													const newSelected = selected.filter(item => item !== id);
													onChange(newSelected);
												}}
											/>
										) : null;
									})}
									{selected.length > 0 && (
										<span className={classes.selectedCount}>
											({selected.length} seleccionado{selected.length > 1 ? 's' : ''})
										</span>
									)}
								</div>
							);
						} else {
							// ✅ SELECCIÓN ÚNICA
							const queue = queues.find(q => q.id === selected);
							
							if (queue && queue.name) {
								return (
									<Chip
										key={selected}
										variant="outlined"
										style={{ backgroundColor: queue.color }}
										label={queue.name}
										className={classes.chip}
									/>
								);
							} else {
								return <span className={classes.placeholder}>Seleccionar departamento...</span>;
							}
						}
					}}
				>
					{!multiple && <MenuItem value="">Ninguna</MenuItem>}
					{queues.map(queue => {
						const isSelected = multiple 
							? selectedQueueIds && selectedQueueIds.includes(queue.id)
							: selectedQueueIds === queue.id;
						
						// ✅ NUEVO: Detectar si el departamento tiene automatización (incluyendo FlowBuilder)
						const hasPrompt = queue.promptId !== null && queue.promptId !== undefined;
						const hasIntegration = queue.integrationId !== null && queue.integrationId !== undefined;
						const hasChatbot = queue.options && Array.isArray(queue.options) && queue.options.length > 0;
						const hasFlowBuilder = queue.flowBuilderId !== null && queue.flowBuilderId !== undefined;
						const hasAutomation = hasPrompt || hasIntegration || hasChatbot || hasFlowBuilder;
						const automationText = hasAutomation ? "Con automatización" : "Sin automatización";
						
						return (
							<MenuItem 
								key={queue.id} 
								value={queue.id}
								className={isSelected ? classes.menuItemSelected : classes.menuItem}
							>
								<div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
									<span 
										className={classes.menuItemIcon}
										style={{ color: queue.color }}
									>
										{isSelected ? '✓' : '○'}
									</span>
									<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
										<span style={{ fontWeight: 'bold', fontSize: '14px' }}>
											{queue.name}
										</span>
										<span style={{ fontSize: '12px', color: '#666' }}>
											{automationText}
										</span>
									</div>
									{isSelected && (
										<span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#1976d2' }}>
											Seleccionado
										</span>
									)}
								</div>
							</MenuItem>
						);
					})}
				</Select>
			</FormControl>
		</div>
	);
};

export default QueueSelect;
